import { useCallback } from 'react';
import { createBridgeChannel } from './createBridgeChannel';
import { SIDER_FOREGROUND_DENIED_CHANNEL, SIDER_OCCUPANCY_CHANNEL, SIDER_REQUEST_FOREGROUND_CHANNEL, } from '../types/siderChannel';
/**
 * Cross-app channels for the "single right-hand sider" policy, built on the
 * generic {@link createBridgeChannel} primitive over the shell-owned
 * {@link ShellComposerBridge}. See `types/siderChannel.ts` for the policy, the
 * payload contracts, and the backwards-compatibility rules.
 *
 * Three channels:
 *   - request-foreground — an MFE sider asks to take the slot; the shell
 *     composer listens and yields (closing itself, running its unsaved-changes
 *     guard) when it holds the slot.
 *   - foreground-denied — the current holder declined to yield (e.g. the user
 *     backed out of the discard dialog); requesters drop their pending open.
 *   - occupancy — an MFE reports whether its own sider currently occupies the
 *     slot, so the shell can pick an instant swap (handoff) vs a slide (empty
 *     slot) when opening the composer. Presentation only; never gates behavior.
 *
 * The wire payload carries the `SiderId` (plus `occupied` for occupancy); the
 * wrappers below unwrap it to the bare arguments the public API exposes. Prefer
 * the hooks in React code — the bridge-taking `emit`/`on` forms are for tests
 * holding an explicit bridge.
 */
var requestForeground = createBridgeChannel(SIDER_REQUEST_FOREGROUND_CHANNEL);
var foregroundDenied = createBridgeChannel(SIDER_FOREGROUND_DENIED_CHANNEL);
var occupancy = createBridgeChannel(SIDER_OCCUPANCY_CHANNEL);
/**
 * Announce that the sider identified by `id` wants to take the right-hand
 * slot. The shell composer listens and yields the slot (closing itself, with
 * its unsaved-changes guard) when it is the current holder.
 */
export function emitSiderRequestForeground(bridge, id) {
    requestForeground.emit(bridge, { id: id });
}
/**
 * Subscribe to foreground requests. Returns an unsubscribe function. Prefer
 * `useSiderRequestForeground` in React components.
 */
export function onSiderRequestForeground(bridge, listener) {
    return requestForeground.subscribe(bridge, function (_a) {
        var id = _a.id;
        return listener(id);
    });
}
/**
 * Announce that the current holder (`id`) declined to yield the slot — e.g.
 * the user backed out of the composer's discard dialog. Requesters use this to
 * drop a pending open.
 */
export function emitSiderForegroundDenied(bridge, id) {
    foregroundDenied.emit(bridge, { id: id });
}
/**
 * Subscribe to foreground denials. Returns an unsubscribe function. Used
 * internally by `useSiderArbitration`.
 */
export function onSiderForegroundDenied(bridge, listener) {
    return foregroundDenied.subscribe(bridge, function (_a) {
        var id = _a.id;
        return listener(id);
    });
}
/**
 * Subscribe to MFE requests to take the right-hand sider slot. The shell uses
 * this to close its event-composer sider (running the composer's
 * unsaved-changes guard) when an MFE sider wants to open. The latest `listener`
 * runs on every request; the subscription is set up once per bridge instance.
 */
export function useSiderRequestForeground(listener) {
    requestForeground.useSubscribe(function (_a) {
        var id = _a.id;
        return listener(id);
    });
}
/**
 * Returns a stable callback that announces this MFE sider wants the slot.
 * Reads the shell bridge from context so callers don't thread it manually.
 */
export function useEmitSiderRequestForeground() {
    var emit = requestForeground.useEmit();
    return useCallback(function (id) { return emit({ id: id }); }, [emit]);
}
/**
 * Subscribe to foreground denials from inside a React component. Used by
 * `useSiderArbitration` to drop a pending open when the composer declines to
 * yield.
 */
export function useSiderForegroundDenied(listener) {
    foregroundDenied.useSubscribe(function (_a) {
        var id = _a.id;
        return listener(id);
    });
}
/**
 * Returns a stable callback for the current slot holder (the shell composer) to
 * announce it declined to yield — e.g. the user backed out of the discard
 * dialog. Reads the shell bridge from context.
 */
export function useEmitSiderForegroundDenied() {
    var emit = foregroundDenied.useEmit();
    return useCallback(function (id) { return emit({ id: id }); }, [emit]);
}
/**
 * Report whether the sider identified by `id` currently occupies the slot, and
 * optionally whether it shows its own Robin Agent entry point (see
 * `SiderOccupancyDetail`).
 * Prefer `useEmitSiderOccupancy` in React components.
 */
export function emitSiderOccupancy(bridge, id, occupied, displaysOwnAgentEntryPoint) {
    occupancy.emit(bridge, { id: id, occupied: occupied, displaysOwnAgentEntryPoint: displaysOwnAgentEntryPoint });
}
/**
 * Subscribe to occupancy reports. Returns an unsubscribe function. Prefer
 * `useSiderOccupancy` in React components.
 */
export function onSiderOccupancy(bridge, listener) {
    return occupancy.subscribe(bridge, function (_a) {
        var id = _a.id, occupied = _a.occupied, displaysOwnAgentEntryPoint = _a.displaysOwnAgentEntryPoint;
        return listener(id, occupied, displaysOwnAgentEntryPoint);
    });
}
/**
 * Subscribe to MFE sider-occupancy reports. The shell uses this to choose
 * between an instant swap and a slide when opening the composer. The latest
 * `listener` runs on every report; the subscription is set up once per bridge.
 */
export function useSiderOccupancy(listener) {
    occupancy.useSubscribe(function (_a) {
        var id = _a.id, occupied = _a.occupied, displaysOwnAgentEntryPoint = _a.displaysOwnAgentEntryPoint;
        return listener(id, occupied, displaysOwnAgentEntryPoint);
    });
}
/**
 * Returns a stable callback for an MFE to report whether its sider occupies the
 * slot. Reads the shell bridge from context so callers don't thread it manually.
 */
export function useEmitSiderOccupancy() {
    var emit = occupancy.useEmit();
    return useCallback(function (id, occupied, displaysOwnAgentEntryPoint) {
        return emit({ id: id, occupied: occupied, displaysOwnAgentEntryPoint: displaysOwnAgentEntryPoint });
    }, [emit]);
}
